import React from "react";
import {
  Anchor,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { Link, useParams } from "react-router-dom";
import { useDelete, useOne } from "@refinedev/core";

import type { Restaurant, Menu } from "../../types";

export function RestaurantShow() {
  const { id } = useParams();
  const { mutateAsync: deleteAsync, isLoading: isDeleting } = useDelete();

  const { data, isLoading } = useOne<Restaurant>({
    resource: "restaurants",
    id: id as string,
  });

  const restaurant = data?.data;

  /**
   * Keep delete behavior consistent with the admin menus list.
   * This removes the menu and related menu data after confirmation.
   */
  const handleDeleteMenu = async (menu: Menu) => {
    const label = restaurant?.name ?? menu.id;
    const confirmed = window.confirm(
      `Delete menu for "${label}"? This will permanently remove the menu, its pages, items, and query history.`
    );
    if (!confirmed) return;

    try {
      await deleteAsync({ resource: "menus", id: menu.id });
      notifications.show({
        title: "Menu deleted",
        message: `Menu for "${label}" has been deleted.`,
        color: "green",
      });
    } catch (e: unknown) {
      const message =
        e && typeof e === "object" && "message" in e
          ? String((e as { message: unknown }).message)
          : "Failed to delete menu";
      notifications.show({
        title: "Delete failed",
        message,
        color: "red",
      });
    }
  };

  if (isLoading) {
    return (
      <Group justify="center" py="xl">
        <Loader />
      </Group>
    );
  }

  if (!restaurant) {
    return <Text>Restaurant not found.</Text>;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>{restaurant.name ?? "(Unnamed restaurant)"}</Title>
        <Anchor component={Link} to={`/admin/restaurants/edit/${restaurant.id}`}>
          Edit restaurant
        </Anchor>
      </Group>

      <Card withBorder>
        <Stack gap="xs">
          <Text fw={600}>Description</Text>
          <Text c="dimmed">{restaurant.description ?? "—"}</Text>

          <Divider my="xs" />

          <Text fw={600}>Admin notes</Text>
          <Text c="dimmed" style={{ whiteSpace: "pre-wrap" }}>
            {restaurant.adminNotes ?? "—"}
          </Text>
        </Stack>
      </Card>

      <Stack gap="xs">
        <Title order={3}>Menus</Title>
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Menu ID</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th>Updated</Table.Th>
              <Table.Th style={{ width: 200 }}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(restaurant.menus ?? []).map((menu: Menu) => (
              <Table.Tr key={menu.id}>
                <Table.Td>
                  <Text size="sm" style={{ fontFamily: "monospace" }}>
                    {menu.id}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {menu.createdAt ? new Date(menu.createdAt).toLocaleString() : "—"}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {menu.updatedAt ? new Date(menu.updatedAt).toLocaleString() : "—"}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <Anchor component={Link} to={`/admin/menus/edit/${menu.id}`}>
                      Edit
                    </Anchor>
                    <Anchor component={Link} to={`/menus/v2/${menu.id}`}>
                      Public
                    </Anchor>
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="red"
                      loading={isDeleting}
                      onClick={() => handleDeleteMenu(menu)}
                    >
                      Delete
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </Stack>
  );
}
